<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MultilatThemeAPI from 'dashboard/api/multilatTheme';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const isLoading = ref(false);
const isSaving = ref(false);
const isUploadingBg = ref(false);

// Theme Settings State
const headerStyle = ref('colorized');
const homeBgType = ref('image');
const homeBgPreset = ref('office-01');
const homeBgCustomUrl = ref(null);
const homeBgOverlayOpacity = ref(70);
const bubbleStyle = ref('circular');
const messageBgPattern = ref('dots');
const messageBgSpacing = ref(20);
const messageBgOpacityLight = ref(10);
const messageBgOpacityDark = ref(5);
const patternPreviewIsDark = ref(false);
const backgroundUrl = ref(null);
const availablePresets = ref([]);

// Background Fade Settings
const homeBgFadeDesktop = ref(false);
const homeBgFadeMobile = ref(true);

// Bubble Icon Settings
const bubbleIcon = ref('multilat');
const bubbleIconCustomPath = ref('');
const bubbleIconCustomViewbox = ref('0 0 240 240');

const bubbleIconPresets = {
  multilat: {
    path: 'M243.5-.3H12.5C5.6-.3,0,5.4,0,12.2v231C0,250.1,5.6,255.7,12.5,255.7h81.8l24.9,24.9c4.9,4.9,12.8,4.9,17.7,0l24.9-24.9h81.8c6.9,0,12.5-5.6,12.5-12.5V12.2c0-6.9-5.6-12.5-12.5-12.5ZM201.6,156.6c-.5,3.4-2.5,6.4-5.3,8.3-19.2,16.8-43.6,26.4-69.1,27.2-25.5-.9-49.9-10.5-69.1-27.3-3-2.8-4.4-7-3.7-11.1.7-4.1,3.5-7.5,7.3-9.2,3.8-1.6,8.2-1.2,11.6,1.1,36.8,29.4,70.9,29.4,107.7,0h0c2.5-2.2,5.9-3.4,9.3-3.2,3.4.2,6.6,1.9,8.8,4.6,2.1,2.7,3.1,6.2,2.5,9.6Z',
    viewBox: '0 -1 256 282',
  },
  classic: {
    path: 'M240.808 240.808H122.123C56.6994 240.808 3.45695 187.562 3.45695 122.122C3.45695 56.7031 56.6994 3.45697 122.124 3.45697C187.566 3.45697 240.808 56.7031 240.808 122.122V240.808Z',
    viewBox: '0 0 240 240',
  },
};

const previewBubbleIcon = computed(() => {
  if (bubbleIcon.value === 'custom' && bubbleIconCustomPath.value) {
    return {
      path: bubbleIconCustomPath.value,
      viewBox: bubbleIconCustomViewbox.value || '0 0 240 240',
    };
  }
  return bubbleIconPresets[bubbleIcon.value] || bubbleIconPresets.multilat;
});

// Branding Footer Settings
const brandingText = ref('Powered By <b>Multilat</b>');
const brandingLogoUrl = ref('/multilat-theme/Multilat-Logo-Icon-Only.svg');
const brandingLinkUrl = ref('https://multilat.xyz');

const opacityDisplay = computed(() => `${homeBgOverlayOpacity.value}%`);

function patternPreviewStyle(pattern) {
  const dark = patternPreviewIsDark.value;
  const bg = dark ? 'background-color: #1e293b;' : 'background-color: #f8f9fa;';
  const c = dark ? '255,255,255' : '0,0,0';
  const hex = dark ? 'ffffff' : '000000';
  const o = dark ? '0.12' : '0.12';
  const so = dark ? '0.20' : '0.15';
  switch (pattern) {
    case 'dots':
      return `${bg} background-image: radial-gradient(circle, rgba(${c},${o}) 1px, transparent 1px); background-size: 8px 8px;`;
    case 'grid':
      return `${bg} background-image: linear-gradient(rgba(${c},${o}) 1px, transparent 1px), linear-gradient(90deg, rgba(${c},${o}) 1px, transparent 1px); background-size: 10px 10px;`;
    case 'shapes':
      return `${bg} background-image: url("data:image/svg+xml,%3Csvg width='30' height='30' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23${hex}' fill-opacity='${o}'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");`;
    case 'diagonal':
      return `${bg} background-image: repeating-linear-gradient(45deg, rgba(${c},${o}) 0, rgba(${c},${o}) 1px, transparent 1px, transparent 8px);`;
    case 'lines':
      return `${bg} background-image: repeating-linear-gradient(0deg, rgba(${c},${o}) 0, rgba(${c},${o}) 1px, transparent 1px, transparent 8px);`;
    case 'polka':
      return `${bg} background-image: radial-gradient(circle, rgba(${c},${o}) 1.5px, transparent 1.5px), radial-gradient(circle, rgba(${c},${o}) 1.5px, transparent 1.5px); background-size: 10px 10px; background-position: 0 0, 5px 5px;`;
    case 'zigzag':
      return `${bg} background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40'%3E%3Cpath d='M0 10 L10 0 L20 10 L30 0 L40 10' fill='none' stroke='%23${hex}' stroke-opacity='${so}' stroke-width='1'/%3E%3Cpath d='M0 30 L10 20 L20 30 L30 20 L40 30' fill='none' stroke='%23${hex}' stroke-opacity='${so}' stroke-width='1'/%3E%3C/svg%3E"); background-size: 16px 16px;`;
    case 'wavy':
      return `${bg} background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40'%3E%3Cpath d='M0 10 Q10 0 20 10 T40 10' fill='none' stroke='%23${hex}' stroke-opacity='${so}' stroke-width='1'/%3E%3Cpath d='M0 30 Q10 20 20 30 T40 30' fill='none' stroke='%23${hex}' stroke-opacity='${so}' stroke-width='1'/%3E%3C/svg%3E"); background-size: 16px 16px;`;
    default:
      return bg;
  }
}

function applySettingsFromResponse(data) {
  const s = data.theme_settings || {};
  headerStyle.value = s.header_style || 'colorized';
  homeBgType.value = s.home_bg_type || 'image';
  homeBgPreset.value = s.home_bg_preset || 'office-01';
  homeBgCustomUrl.value = s.home_bg_custom_url || null;
  homeBgOverlayOpacity.value = s.home_bg_overlay_opacity ?? 70;
  const VALID_BUBBLE_STYLES = ['circular', 'modern', 'flat'];
  bubbleStyle.value = VALID_BUBBLE_STYLES.includes(s.bubble_style)
    ? s.bubble_style
    : 'circular';
  messageBgPattern.value = s.message_bg_pattern || 'dots';
  messageBgSpacing.value = s.message_bg_spacing ?? 20;
  messageBgOpacityLight.value = s.message_bg_opacity_light ?? 10;
  messageBgOpacityDark.value = s.message_bg_opacity_dark ?? 5;
  homeBgFadeDesktop.value = s.home_bg_fade_desktop ?? false;
  homeBgFadeMobile.value = s.home_bg_fade_mobile ?? true;
  bubbleIcon.value = s.bubble_icon || 'multilat';
  bubbleIconCustomPath.value = s.bubble_icon_custom_path || '';
  bubbleIconCustomViewbox.value = s.bubble_icon_custom_viewbox || '0 0 240 240';
  backgroundUrl.value = data.background_url || null;
  brandingText.value = s.branding_text || 'Powered By <b>Multilat</b>';
  brandingLogoUrl.value =
    s.branding_logo_url ?? '/multilat-theme/Multilat-Logo-Icon-Only.svg';
  brandingLinkUrl.value = s.branding_link_url || 'https://multilat.xyz';
  if (data.available_presets) {
    availablePresets.value = data.available_presets;
  }
}

async function fetchThemeSettings() {
  isLoading.value = true;
  try {
    const { data } = await MultilatThemeAPI.show(props.inbox.id);
    applySettingsFromResponse(data);
  } catch {
    useAlert('Failed To Load Theme Settings');
  } finally {
    isLoading.value = false;
  }
}

async function saveThemeSettings() {
  isSaving.value = true;
  try {
    const payload = {
      header_style: headerStyle.value,
      home_bg_type: homeBgType.value,
      home_bg_preset: homeBgPreset.value,
      home_bg_overlay_opacity: homeBgOverlayOpacity.value,
      bubble_style: bubbleStyle.value,
      message_bg_pattern: messageBgPattern.value,
      message_bg_spacing: messageBgSpacing.value,
      message_bg_opacity_light: messageBgOpacityLight.value,
      message_bg_opacity_dark: messageBgOpacityDark.value,
      home_bg_fade_desktop: homeBgFadeDesktop.value,
      home_bg_fade_mobile: homeBgFadeMobile.value,
      bubble_icon: bubbleIcon.value,
      bubble_icon_custom_path: bubbleIconCustomPath.value,
      bubble_icon_custom_viewbox: bubbleIconCustomViewbox.value,
      branding_text: brandingText.value,
      branding_logo_url: brandingLogoUrl.value,
      branding_link_url: brandingLinkUrl.value,
    };
    const { data } = await MultilatThemeAPI.update(props.inbox.id, payload);
    applySettingsFromResponse(data);
    useAlert('Theme Settings Saved Successfully');
  } catch {
    useAlert('Failed To Save Theme Settings');
  } finally {
    isSaving.value = false;
  }
}

async function handleBackgroundUpload(event) {
  const file = event.target.files[0];
  if (!file) return;

  isUploadingBg.value = true;
  try {
    const { data } = await MultilatThemeAPI.uploadBackground(
      props.inbox.id,
      file
    );
    backgroundUrl.value = data.background_url;
    homeBgCustomUrl.value = data.theme_settings?.home_bg_custom_url || null;
    homeBgType.value = 'image';
    homeBgPreset.value = null;
    useAlert('Background Image Uploaded Successfully');
  } catch {
    useAlert('Failed To Upload Background Image');
  } finally {
    isUploadingBg.value = false;
    event.target.value = '';
  }
}

async function removeBackground() {
  try {
    await MultilatThemeAPI.deleteBackground(props.inbox.id);
    backgroundUrl.value = null;
    homeBgCustomUrl.value = null;
    homeBgType.value = 'gradient';
    useAlert('Background Image Removed');
  } catch {
    useAlert('Failed To Remove Background Image');
  }
}

function selectPreset(presetKey) {
  homeBgPreset.value = presetKey;
  homeBgCustomUrl.value = null;
  homeBgType.value = 'image';
}

onMounted(() => {
  fetchThemeSettings();
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div>
    <div v-if="isLoading" class="py-12 text-center text-n-slate-11">
      Loading Theme Settings...
    </div>

    <div v-else>
      <!-- Header Style -->
      <div class="py-6 mx-8 border-b border-n-weak/60">
        <div class="grid grid-cols-1 lg:grid-cols-8 gap-6">
          <div class="col-span-3">
            <p class="text-base text-n-brand mb-0 font-medium">Header Style</p>
            <p
              class="text-sm mb-2 text-n-slate-11 leading-5 tracking-normal mt-2"
            >
              Choose How The Widget Header Appears. Colorized Uses Your Widget
              Color As The Header Background.
            </p>
          </div>
          <div class="col-span-5">
            <div class="flex flex-col gap-2">
              <label
                class="flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors"
                :class="
                  headerStyle === 'colorized'
                    ? 'border-woot-500 bg-woot-25 dark:bg-woot-800/10'
                    : 'border-n-weak hover:border-n-slate-6'
                "
              >
                <input
                  v-model="headerStyle"
                  type="radio"
                  value="colorized"
                  class="accent-woot-500"
                />
                <div>
                  <p class="text-sm font-medium text-n-slate-12 mb-0">
                    Colorized
                  </p>
                  <p class="text-xs text-n-slate-11 mb-0">
                    Widget Color As Header Background With White Text
                  </p>
                </div>
              </label>
              <label
                class="flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors"
                :class="
                  headerStyle === 'plain'
                    ? 'border-woot-500 bg-woot-25 dark:bg-woot-800/10'
                    : 'border-n-weak hover:border-n-slate-6'
                "
              >
                <input
                  v-model="headerStyle"
                  type="radio"
                  value="plain"
                  class="accent-woot-500"
                />
                <div>
                  <p class="text-sm font-medium text-n-slate-12 mb-0">Plain</p>
                  <p class="text-xs text-n-slate-11 mb-0">
                    Default Neutral Header Style
                  </p>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- Home Background -->
      <div class="py-6 mx-8 border-b border-n-weak/60">
        <div class="grid grid-cols-1 lg:grid-cols-8 gap-6">
          <div class="col-span-3">
            <p class="text-base text-n-brand mb-0 font-medium">
              Home Background
            </p>
            <p
              class="text-sm mb-2 text-n-slate-11 leading-5 tracking-normal mt-2"
            >
              Set The Background For The Widget Home/Welcome View.
            </p>
          </div>
          <div class="col-span-5">
            <div class="flex flex-col gap-4">
              <!-- Type Selector (Segmented Control) -->
              <div
                class="inline-flex rounded-lg bg-n-slate-3 dark:bg-n-slate-4 p-1.5 gap-0.5"
              >
                <button
                  v-for="opt in [
                    { value: 'plain', label: 'Plain' },
                    { value: 'gradient', label: 'Gradient' },
                    { value: 'image', label: 'Background Image' },
                  ]"
                  :key="opt.value"
                  type="button"
                  class="px-3.5 py-1.5 text-sm font-medium rounded-md transition-all"
                  :class="
                    homeBgType === opt.value
                      ? 'bg-woot-500 text-white shadow-sm'
                      : 'text-n-slate-11 hover:text-n-slate-12'
                  "
                  @click="homeBgType = opt.value"
                >
                  {{ opt.label }}
                </button>
              </div>

              <!-- Gradient Info -->
              <p
                v-if="homeBgType === 'gradient'"
                class="text-sm text-n-slate-11 mb-0"
              >
                A Gradient Will Be Auto-Generated From Your Widget Color.
              </p>

              <!-- Plain Info -->
              <p
                v-if="homeBgType === 'plain'"
                class="text-sm text-n-slate-11 mb-0"
              >
                No Special Background — The Default Widget Background Will Be
                Used.
              </p>

              <!-- Image Options -->
              <div v-if="homeBgType === 'image'" class="flex flex-col gap-4">
                <!-- Preset Grid -->
                <div>
                  <p class="text-sm font-medium text-n-slate-12 mb-2">
                    Preset Backgrounds
                  </p>
                  <div class="grid grid-cols-3 gap-3">
                    <button
                      v-for="preset in availablePresets"
                      :key="preset.key"
                      type="button"
                      class="relative aspect-video rounded-lg p-0 transition-all hover:opacity-90"
                      @click="selectPreset(preset.key)"
                    >
                      <img
                        :src="preset.url"
                        :alt="preset.label"
                        class="block w-full h-full rounded-lg object-cover"
                      />
                      <!-- Border Overlay -->
                      <span
                        class="absolute inset-0 rounded-lg pointer-events-none"
                        :class="
                          homeBgPreset === preset.key && !homeBgCustomUrl
                            ? 'border-[3px] border-n-blue-10'
                            : 'border border-n-slate-6'
                        "
                      />
                      <!-- Active Checkmark -->
                      <span
                        v-if="homeBgPreset === preset.key && !homeBgCustomUrl"
                        class="absolute inset-0 rounded-lg flex items-center justify-center bg-black/20"
                      >
                        <span
                          class="w-8 h-8 rounded-full bg-n-blue-10 flex items-center justify-center shadow-lg"
                        >
                          <svg
                            class="w-5 h-5 text-white"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2.5"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </span>
                      </span>
                      <span
                        class="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1 rounded-b-lg"
                      >
                        <span class="text-xs text-white font-medium">
                          {{ preset.label }}
                        </span>
                      </span>
                    </button>
                  </div>
                </div>

                <!-- Custom Upload -->
                <div>
                  <p class="text-sm font-medium text-n-slate-12 mb-2">
                    or Upload Custom Background
                  </p>
                  <div class="flex items-center gap-3">
                    <label
                      class="px-4 py-2 text-sm font-medium rounded-lg border border-n-weak text-n-slate-12 hover:border-n-slate-6 cursor-pointer transition-colors"
                    >
                      {{ isUploadingBg ? 'Uploading...' : 'Choose Image' }}
                      <input
                        type="file"
                        accept="image/*"
                        class="hidden"
                        :disabled="isUploadingBg"
                        @change="handleBackgroundUpload"
                      />
                    </label>
                    <NextButton
                      v-if="homeBgCustomUrl || backgroundUrl"
                      variant="ghost"
                      color="ruby"
                      size="sm"
                      label="Remove"
                      @click="removeBackground"
                    />
                  </div>
                  <!-- Custom Image Preview -->
                  <div
                    v-if="homeBgCustomUrl"
                    class="mt-2 w-48 aspect-video rounded-lg overflow-hidden border-2 border-woot-500"
                  >
                    <img
                      :src="homeBgCustomUrl"
                      alt="Custom Background"
                      class="w-full h-full object-cover"
                    />
                  </div>
                </div>

                <!-- Overlay Opacity -->
                <div>
                  <p class="text-sm font-medium text-n-slate-12 mb-1">
                    Color Overlay Opacity: {{ opacityDisplay }}
                  </p>
                  <p class="text-xs text-n-slate-11 mb-2">
                    Controls How Much of Your Widget Color Overlays The
                    Background Image
                  </p>
                  <input
                    v-model.number="homeBgOverlayOpacity"
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    class="w-full h-2 bg-n-slate-3 rounded-lg appearance-none cursor-pointer accent-woot-500"
                  />
                  <div
                    class="flex justify-between text-xs text-n-slate-10 mt-1"
                  >
                    <span>0% (No Overlay)</span>
                    <span>100% (Full Color)</span>
                  </div>
                </div>
              </div>

              <!-- Background Fade Settings -->
              <div
                v-if="homeBgType === 'gradient' || homeBgType === 'image'"
                class="flex flex-col gap-3 p-4 rounded-lg border border-n-weak bg-n-slate-2 dark:bg-n-solid-2"
              >
                <div>
                  <p class="text-sm font-medium text-n-slate-12 mb-1">
                    Background Fade
                  </p>
                  <p class="text-xs text-n-slate-11 mb-0">
                    Controls Whether The Background Fades Out Gradually or Has A
                    Hard Edge. When Articles Are Present on Desktop, Fade Is
                    Always Applied.
                  </p>
                </div>
                <label
                  class="flex items-center gap-3 cursor-pointer select-none"
                  @click="homeBgFadeDesktop = !homeBgFadeDesktop"
                >
                  <span
                    class="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 ease-in-out"
                    :class="
                      homeBgFadeDesktop
                        ? 'bg-woot-500'
                        : 'bg-n-slate-6 dark:bg-n-slate-7'
                    "
                  >
                    <span
                      class="inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200 ease-in-out"
                      :class="
                        homeBgFadeDesktop ? 'translate-x-6' : 'translate-x-1'
                      "
                    />
                  </span>
                  <span class="text-sm text-n-slate-12">
                    Fade on Desktop (No Articles)
                  </span>
                </label>
                <label
                  class="flex items-center gap-3 cursor-pointer select-none"
                  @click="homeBgFadeMobile = !homeBgFadeMobile"
                >
                  <span
                    class="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 ease-in-out"
                    :class="
                      homeBgFadeMobile
                        ? 'bg-woot-500'
                        : 'bg-n-slate-6 dark:bg-n-slate-7'
                    "
                  >
                    <span
                      class="inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200 ease-in-out"
                      :class="
                        homeBgFadeMobile ? 'translate-x-6' : 'translate-x-1'
                      "
                    />
                  </span>
                  <span class="text-sm text-n-slate-12"> Fade on Mobile </span>
                </label>
                <p
                  class="text-xs text-woot-500 dark:text-woot-400 mt-1 mb-0 italic"
                >
                  Changes Take Effect After Saving Theme Settings.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Bubble Style -->
      <div class="py-6 mx-8 border-b border-n-weak/60">
        <div class="grid grid-cols-1 lg:grid-cols-8 gap-6">
          <div class="col-span-3">
            <p class="text-base text-n-brand mb-0 font-medium">Bubble Style</p>
            <p
              class="text-sm mb-2 text-n-slate-11 leading-5 tracking-normal mt-2"
            >
              Choose The Chat Bubble Button Shape That Appears on Your Website.
            </p>
          </div>
          <div class="col-span-5">
            <div class="flex flex-col gap-2">
              <label
                v-for="opt in [
                  {
                    value: 'modern',
                    label: 'Modern Rounded',
                    desc: 'Rounded Rectangle With Soft Corners (16px Radius)',
                  },
                  {
                    value: 'circular',
                    label: 'Circular',
                    desc: 'Classic Circular Chat Bubble',
                  },
                  {
                    value: 'flat',
                    label: 'Flat',
                    desc: 'Minimal Square Shape With Slight Rounding',
                  },
                ]"
                :key="opt.value"
                class="flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors"
                :class="
                  bubbleStyle === opt.value
                    ? 'border-woot-500 bg-woot-25 dark:bg-woot-800/10'
                    : 'border-n-weak hover:border-n-slate-6'
                "
              >
                <input
                  v-model="bubbleStyle"
                  type="radio"
                  :value="opt.value"
                  class="accent-woot-500"
                />
                <div class="flex items-center gap-3">
                  <!-- Visual Preview -->
                  <div
                    class="w-10 h-10 bg-woot-500 flex items-center justify-center flex-shrink-0"
                    :class="{
                      'rounded-2xl': opt.value === 'modern',
                      'rounded-full': opt.value === 'circular',
                      rounded: opt.value === 'flat',
                    }"
                  >
                    <svg
                      class="w-5 h-5 text-white"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p class="text-sm font-medium text-n-slate-12 mb-0">
                      {{ opt.label }}
                    </p>
                    <p class="text-xs text-n-slate-11 mb-0">{{ opt.desc }}</p>
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- Bubble Icon -->
      <div class="py-6 mx-8 border-b border-n-weak/60">
        <div class="grid grid-cols-1 lg:grid-cols-8 gap-6">
          <div class="col-span-3">
            <p class="text-base text-n-brand mb-0 font-medium">Bubble Icon</p>
            <p
              class="text-sm mb-2 text-n-slate-11 leading-5 tracking-normal mt-2"
            >
              Choose The Icon Displayed Inside The Chat Bubble Button.
            </p>
          </div>
          <div class="col-span-5">
            <div class="flex flex-col gap-4">
              <!-- Preset Options -->
              <div class="flex flex-col gap-2">
                <label
                  v-for="opt in [
                    {
                      value: 'multilat',
                      label: 'Multilat',
                      desc: 'Chat Bubble With Smile',
                    },
                    {
                      value: 'classic',
                      label: 'Classic',
                      desc: 'Original Speech Bubble Shape',
                    },
                    {
                      value: 'custom',
                      label: 'Custom',
                      desc: 'Provide Your Own SVG Path',
                    },
                  ]"
                  :key="opt.value"
                  class="flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors"
                  :class="
                    bubbleIcon === opt.value
                      ? 'border-woot-500 bg-woot-25 dark:bg-woot-800/10'
                      : 'border-n-weak hover:border-n-slate-6'
                  "
                >
                  <input
                    v-model="bubbleIcon"
                    type="radio"
                    :value="opt.value"
                    class="accent-woot-500"
                  />
                  <div class="flex items-center gap-3">
                    <!-- Icon Preview -->
                    <div
                      v-if="opt.value !== 'custom'"
                      class="w-10 h-10 bg-woot-500 flex items-center justify-center flex-shrink-0"
                      :class="{
                        'rounded-2xl': bubbleStyle === 'modern',
                        'rounded-full': bubbleStyle === 'circular',
                        rounded: bubbleStyle === 'flat',
                      }"
                    >
                      <svg
                        class="w-5 h-5"
                        :viewBox="bubbleIconPresets[opt.value].viewBox"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          :d="bubbleIconPresets[opt.value].path"
                          fill="#FFFFFF"
                        />
                      </svg>
                    </div>
                    <div>
                      <p class="text-sm font-medium text-n-slate-12 mb-0">
                        {{ opt.label }}
                      </p>
                      <p class="text-xs text-n-slate-11 mb-0">
                        {{ opt.desc }}
                      </p>
                    </div>
                  </div>
                </label>
              </div>

              <!-- Custom Path Inputs -->
              <div v-if="bubbleIcon === 'custom'" class="flex flex-col gap-3">
                <div>
                  <label class="block text-sm font-medium text-n-slate-12 mb-1">
                    SVG Path Data
                  </label>
                  <textarea
                    v-model="bubbleIconCustomPath"
                    rows="3"
                    placeholder="M240.808 240.808H122..."
                    class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-9 focus:border-woot-500 focus:outline-none resize-y font-mono"
                  />
                  <p class="text-xs text-n-slate-11 mt-1 mb-0">
                    The "d" Attribute From An SVG &lt;path&gt; Element.
                  </p>
                </div>
                <div>
                  <label class="block text-sm font-medium text-n-slate-12 mb-1">
                    ViewBox
                  </label>
                  <input
                    v-model="bubbleIconCustomViewbox"
                    type="text"
                    placeholder="0 0 240 240"
                    class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-9 focus:border-woot-500 focus:outline-none font-mono"
                  />
                </div>
              </div>

              <!-- Live Preview -->
              <div>
                <p class="text-sm font-medium text-n-slate-12 mb-2">Preview</p>
                <div class="flex items-center gap-3">
                  <div
                    class="w-14 h-14 bg-woot-500 flex items-center justify-center shadow-lg"
                    :class="{
                      'rounded-2xl': bubbleStyle === 'modern',
                      'rounded-full': bubbleStyle === 'circular',
                      rounded: bubbleStyle === 'flat',
                    }"
                  >
                    <svg
                      class="w-7 h-7"
                      :viewBox="previewBubbleIcon.viewBox"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path :d="previewBubbleIcon.path" fill="#FFFFFF" />
                    </svg>
                  </div>
                  <p class="text-xs text-n-slate-11 mb-0">
                    How The Bubble Button Will Appear on Your Website
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Message Background Pattern -->
      <div class="py-6 mx-8 border-b border-n-weak/60">
        <div class="grid grid-cols-1 lg:grid-cols-8 gap-6">
          <div class="col-span-3">
            <p class="text-base text-n-brand mb-0 font-medium">
              Message Background Pattern
            </p>
            <p
              class="text-sm mb-2 text-n-slate-11 leading-5 tracking-normal mt-2"
            >
              Add A Subtle Pattern Behind The Chat Messages Area.
            </p>
          </div>
          <div class="col-span-5">
            <!-- Pattern Grid — 3 Per Row -->
            <div class="grid grid-cols-3 gap-2">
              <label
                v-for="opt in [
                  { value: 'none', label: 'None' },
                  { value: 'dots', label: 'Dots' },
                  { value: 'grid', label: 'Grid' },
                  { value: 'shapes', label: 'Shapes' },
                  { value: 'diagonal', label: 'Diagonal' },
                  { value: 'lines', label: 'Lines' },
                  { value: 'polka', label: 'Polka' },
                  { value: 'zigzag', label: 'ZigZag' },
                  { value: 'wavy', label: 'Wavy' },
                ]"
                :key="opt.value"
                class="relative rounded-lg cursor-pointer transition-all hover:opacity-90 aspect-square"
                @click="messageBgPattern = opt.value"
              >
                <input
                  v-model="messageBgPattern"
                  type="radio"
                  :value="opt.value"
                  class="hidden"
                />
                <div
                  class="w-full h-full rounded-lg"
                  :style="patternPreviewStyle(opt.value)"
                />
                <!-- Border Overlay -->
                <span
                  class="absolute inset-0 rounded-lg pointer-events-none"
                  :class="
                    messageBgPattern === opt.value
                      ? 'border-[3px] border-n-blue-10'
                      : 'border border-n-slate-6'
                  "
                />
                <!-- Active Checkmark -->
                <span
                  v-if="messageBgPattern === opt.value"
                  class="absolute inset-0 rounded-lg flex items-center justify-center bg-black/20"
                >
                  <span
                    class="w-8 h-8 rounded-full bg-n-blue-10 flex items-center justify-center shadow-lg"
                  >
                    <svg
                      class="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.5"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </span>
                </span>
                <!-- Name Badge -->
                <span
                  class="absolute top-1 right-1 text-[10px] font-medium text-n-slate-11 bg-n-background/80 dark:bg-n-solid-1/80 px-1.5 py-0.5 rounded"
                >
                  {{ opt.label }}
                </span>
              </label>
            </div>

            <!-- Pattern Settings — Single Row -->
            <div
              v-if="messageBgPattern !== 'none'"
              class="mt-3 flex items-center gap-4 p-3 rounded-lg border border-n-weak bg-n-slate-2 dark:bg-n-solid-2"
            >
              <div class="flex items-center gap-2 flex-1 min-w-0">
                <span
                  class="text-xs font-medium text-n-slate-12 whitespace-nowrap"
                >
                  Spacing: {{ messageBgSpacing }}px
                </span>
                <input
                  v-model.number="messageBgSpacing"
                  type="range"
                  min="8"
                  max="60"
                  step="1"
                  class="flex-1 h-1.5 bg-n-slate-3 rounded-lg appearance-none cursor-pointer accent-woot-500"
                />
              </div>
              <div class="flex items-center gap-2 flex-1 min-w-0">
                <span
                  class="text-xs font-medium text-n-slate-12 whitespace-nowrap"
                >
                  Light: {{ messageBgOpacityLight }}%
                </span>
                <input
                  v-model.number="messageBgOpacityLight"
                  type="range"
                  min="1"
                  max="30"
                  step="1"
                  class="flex-1 h-1.5 bg-n-slate-3 rounded-lg appearance-none cursor-pointer accent-woot-500"
                />
              </div>
              <div class="flex items-center gap-2 flex-1 min-w-0">
                <span
                  class="text-xs font-medium text-n-slate-12 whitespace-nowrap"
                >
                  Dark: {{ messageBgOpacityDark }}%
                </span>
                <input
                  v-model.number="messageBgOpacityDark"
                  type="range"
                  min="1"
                  max="30"
                  step="1"
                  class="flex-1 h-1.5 bg-n-slate-3 rounded-lg appearance-none cursor-pointer accent-woot-500"
                />
              </div>
              <!-- Light/Dark Preview Toggle -->
              <button
                type="button"
                class="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-colors"
                :class="
                  patternPreviewIsDark
                    ? 'bg-n-slate-12 text-n-slate-1'
                    : 'bg-n-slate-3 text-n-slate-11 hover:bg-n-slate-4'
                "
                :title="
                  patternPreviewIsDark
                    ? 'Previewing Dark Mode'
                    : 'Previewing Light Mode'
                "
                @click="patternPreviewIsDark = !patternPreviewIsDark"
              >
                <!-- Sun Icon (Light) -->
                <svg
                  v-if="!patternPreviewIsDark"
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  viewBox="0 0 24 24"
                >
                  <circle cx="12" cy="12" r="5" />
                  <path
                    d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"
                  />
                </svg>
                <!-- Moon Icon (Dark) -->
                <svg
                  v-else
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Branding Footer -->
      <div class="py-6 mx-8 border-b border-n-weak/60">
        <div class="grid grid-cols-1 lg:grid-cols-8 gap-6">
          <div class="col-span-3">
            <p class="text-base text-n-brand mb-0 font-medium">
              Branding Footer
            </p>
            <p
              class="text-sm mb-2 text-n-slate-11 leading-5 tracking-normal mt-2"
            >
              Customize The Branding Link Shown At The Bottom of The Widget. Use
              &lt;b&gt; Tags For Bold Text.
            </p>
          </div>
          <div class="col-span-5">
            <div class="flex flex-col gap-4">
              <!-- Branding Text -->
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1">
                  Branding Text
                </label>
                <input
                  v-model="brandingText"
                  type="text"
                  maxlength="100"
                  placeholder="Powered By <b>Multilat</b>"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-9 focus:border-woot-500 focus:outline-none"
                />
                <p class="text-xs text-n-slate-11 mt-1 mb-0">
                  Supports &lt;b&gt; For Bold. Max 100 Characters.
                </p>
              </div>

              <!-- Branding Logo URL -->
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1">
                  Logo URL
                </label>
                <input
                  v-model="brandingLogoUrl"
                  type="text"
                  placeholder="/multilat-theme/Multilat-Logo-Icon-Only.svg"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-9 focus:border-woot-500 focus:outline-none"
                />
                <p class="text-xs text-n-slate-11 mt-1 mb-0">
                  Relative Path or HTTPS URL. Leave Empty To Hide Logo.
                </p>
              </div>

              <!-- Branding Link URL -->
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1">
                  Link URL
                </label>
                <input
                  v-model="brandingLinkUrl"
                  type="text"
                  placeholder="https://multilat.xyz"
                  class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-9 focus:border-woot-500 focus:outline-none"
                />
                <p class="text-xs text-n-slate-11 mt-1 mb-0">
                  Must Start With https://
                </p>
              </div>

              <!-- Live Preview -->
              <div>
                <p class="text-sm font-medium text-n-slate-12 mb-2">Preview</p>
                <div
                  class="flex items-center justify-center gap-1 px-4 py-2 rounded-lg border border-n-weak bg-n-background"
                >
                  <a
                    :href="brandingLinkUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="flex items-center gap-1 text-xs text-n-slate-11 no-underline hover:text-n-slate-12"
                  >
                    <img
                      v-if="brandingLogoUrl"
                      :src="brandingLogoUrl"
                      alt="Logo"
                      class="w-[18px] h-[18px]"
                    />
                    <span v-html="brandingText" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Save Button -->
      <div class="py-6 mx-8">
        <div class="grid grid-cols-1 lg:grid-cols-8 gap-6">
          <div class="col-span-3" />
          <div class="col-span-5">
            <NextButton
              type="submit"
              label="Save Theme Settings"
              :is-loading="isSaving"
              @click="saveThemeSettings"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
